// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmount } from '../../lib/eventLog.js';
import { formatDate } from '../../lib/formatLocale.js';
import { ReconcileMark } from '../ui/ReconcileMark.js';
import type { Transaction, Account } from '../../types.js';

interface TransactionCardProps {
  tx: Transaction;
  accountId: string;
  accounts: Map<string, Account>;
  currency: string;
  runningBalance: number;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onNavigateAccount: (accountId: string) => void;
}

const REVEAL = 132; // px of action tray revealed on swipe-left

// DECISION (Phase 4): a card, not a table row. The primary line carries the two
// things that matter at a glance — payee and signed amount (mono, tabular, color-
// coded exactly like desktop). The secondary line carries date · transfer ·
// reconcile mark. Tapping expands the card *in place* (accordion) rather than
// navigating: on a phone a quick peek at the splits shouldn't cost a screen
// transition, and it keeps your scroll position in a long register. Swiping left
// reveals Duplicate / Delete — the standard iOS/Android list gesture. The gesture
// only engages when horizontal movement dominates, so it never fights vertical
// scroll, and it's a left-swipe originating inside the card, so it doesn't
// conflict with iOS's screen-edge back-swipe.
export function TransactionCard({
  tx,
  accountId,
  accounts,
  currency,
  runningBalance,
  expanded,
  onToggle,
  onEdit,
  onDuplicate,
  onDelete,
  onNavigateAccount,
}: TransactionCardProps) {
  const { t } = useTranslation(['transactions', 'accounting', 'common', 'errors']);
  const [dx, setDx] = useState(0); // current horizontal offset (<= 0)
  const [open, setOpen] = useState(false); // action tray latched open
  const startX = useRef(0);
  const startY = useRef(0);
  const dragging = useRef<'none' | 'horizontal' | 'vertical'>('none');

  const split = tx.splits.find((s) => s.accountId === accountId);
  const amount = split?.amount ?? 0;
  // Color by direction of money for THIS account: outflows (negative) are
  // negative-red, inflows stay default text. Matches the desktop convention and
  // the Phase 4 mockup (expense −$4.75 red, paycheck +$2,500 default).
  const isOutflow = amount < 0;

  function transferLabel(): string {
    const others = tx.splits.filter((s) => s.accountId !== accountId);
    if (others.length === 0) return '—';
    if (others.length > 1) return t('card.split');
    return accounts.get(others[0]!.accountId)?.name ?? '—';
  }

  function onTouchStart(e: React.TouchEvent) {
    const t = e.touches[0]!;
    startX.current = t.clientX;
    startY.current = t.clientY;
    dragging.current = 'none';
  }
  function onTouchMove(e: React.TouchEvent) {
    const t = e.touches[0]!;
    const mx = t.clientX - startX.current;
    const my = t.clientY - startY.current;
    if (dragging.current === 'none') {
      if (Math.abs(mx) > 8 || Math.abs(my) > 8) {
        dragging.current = Math.abs(mx) > Math.abs(my) ? 'horizontal' : 'vertical';
      }
    }
    if (dragging.current === 'horizontal') {
      const base = open ? -REVEAL : 0;
      const next = Math.min(0, Math.max(-REVEAL, base + mx));
      setDx(next);
    }
  }
  function onTouchEnd() {
    if (dragging.current === 'horizontal') {
      const latched = dx < -REVEAL / 2;
      setOpen(latched);
      setDx(latched ? -REVEAL : 0);
    }
    dragging.current = 'none';
  }

  function handleTap() {
    if (open) {
      setOpen(false);
      setDx(0);
      return;
    }
    onToggle();
  }

  return (
    <div className="relative overflow-hidden border-b border-border bg-bg">
      {/* Action tray behind the card */}
      <div className="absolute inset-y-0 right-0 flex">
        <button
          onClick={() => {
            setOpen(false);
            setDx(0);
            onDuplicate();
          }}
          className="w-16 flex items-center justify-center text-xs text-text bg-bg-elevated active:opacity-80"
        >
          {t('common:actions.duplicate')}
        </button>
        <button
          onClick={() => {
            setOpen(false);
            setDx(0);
            onDelete();
          }}
          className="w-16 flex items-center justify-center text-xs text-white bg-negative active:opacity-80"
        >
          {t('common:actions.delete')}
        </button>
      </div>

      {/* Foreground card */}
      <div
        className="relative bg-bg"
        style={{ transform: `translateX(${dx}px)`, transition: dragging.current === 'horizontal' ? 'none' : 'transform 0.18s ease' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button
          onClick={handleTap}
          className={`w-full text-left px-4 py-3 active:bg-bg-elevated ${tx.voided ? 'opacity-40' : ''}`}
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className={`text-md text-text truncate ${tx.voided ? 'line-through' : ''}`}>
              {tx.description || '—'}
            </span>
            <span className={`text-md font-mono tabular-nums shrink-0 ${isOutflow ? 'text-negative' : 'text-text'}`}>
              {tx.voided ? '—' : formatAmount(amount, currency)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 mt-1">
            <span className="text-sm text-text-muted truncate">
              {formatShortDate(tx.date)} · {transferLabel()}
              {tx.voided && <span className="text-negative"> · {t('register.voidBadge')}</span>}
            </span>
            <span className="shrink-0">
              <ReconcileMark state={split?.reconcileState ?? 'n'} size="sm" />
            </span>
          </div>
        </button>

        {/* Inline expanded detail */}
        {expanded && (
          <div className="px-4 pb-3 bg-bg-elevated border-t border-border">
            <p className="text-xs text-text-subtle uppercase tracking-wide pt-2 pb-1">{t('card.splits')}</p>
            <ul className="space-y-1">
              {tx.splits.map((s) => {
                const acc = accounts.get(s.accountId);
                const cur = acc?.currency ?? currency;
                return (
                  <li key={s.splitId} className="flex items-center justify-between gap-2 text-sm">
                    <button
                      onClick={() => s.accountId !== accountId && onNavigateAccount(s.accountId)}
                      className={`truncate ${s.accountId === accountId ? 'text-text-muted' : 'text-accent active:text-accent-hover'}`}
                    >
                      {acc?.name ?? s.accountId}
                    </button>
                    <span className="font-mono tabular-nums text-text shrink-0">
                      {formatAmount(s.amount, cur)}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center gap-4 pt-3 mt-1 border-t border-border">
              <span className="text-sm text-text-muted">
                {t('accounting:columns.balance')} <span className="font-mono text-text">{formatAmount(runningBalance, currency)}</span>
              </span>
              {!tx.voided && (
                <div className="ml-auto flex items-center gap-4">
                  <button onClick={onEdit} className="text-sm text-accent active:text-accent-hover">{t('common:actions.edit')}</button>
                  <button onClick={onDuplicate} className="text-sm text-text-muted active:text-text">{t('common:actions.duplicate')}</button>
                  <button onClick={onDelete} className="text-sm text-negative active:opacity-80">{t('common:actions.delete')}</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  return formatDate(dateStr, { month: 'short', day: 'numeric' });
}
